<script setup>
const props = defineProps({
  card: Object,
  /** Stable unique suffix so banner + page cards with the same promo id do not share modal DOM ids. */
  instanceKey: {
    type: String,
    default: '',
  },
});

const { fetchPromotionDetail } = usePromotionData();
const { altFallback } = usePageAltFallback();
const promotionImageAlt = computed(() =>
  resolveMediaAlt(
    props.card?.attributes?.image,
    props.card?.attributes?.title,
    altFallback.value,
    'Promotion image'
  )
);

const btnText = props.card?.attributes?.btnText ?? '';
const mobileButtonText = ref(btnText);

const idSuffix = computed(() => props.instanceKey || String(props.card.id));
const modalId = computed(() => `details-${idSuffix.value}`);
const collapseId = computed(() => `cardDescription-${idSuffix.value}`);

const detailsHtml = ref(null);
const detailsLoading = ref(false);
const detailsFetchError = ref(false);

let detachDetailsListeners = null;

const applyDetailPayload = (payload) => {
  const html = payload?.longDescription;
  if (payload?.statusCode != null && payload.statusCode !== 200) return null;
  return typeof html === 'string' && html.length > 0 ? html : null;
};

const embeddedDetailsHtml = () => {
  const source = props.card.attributes.detailSource === 'pulse' ? 'pulse' : 'promo';
  const html = source === 'pulse'
    ? props.card.attributes.discountDescription
    : props.card.attributes.longDescription;
  return typeof html === 'string' && html.length > 0 ? html : null;
};

const loadDetails = async () => {
  if (detailsHtml.value) return;
  if (detailsLoading.value) return;
  if (detailsFetchError.value) return;

  const embeddedHtml = embeddedDetailsHtml();
  if (embeddedHtml) {
    detailsHtml.value = embeddedHtml;
    return;
  }

  try {
    detailsLoading.value = true;
    const source = props.card.attributes.detailSource === 'pulse' ? 'pulse' : 'promo';
    const payload = await fetchPromotionDetail(props.card.id, source);
    detailsHtml.value = applyDetailPayload(payload);
    if (!detailsHtml.value) detailsFetchError.value = true;
  } catch (loadError) {
    detailsFetchError.value = true;
    if (import.meta.dev) {
      console.warn('[PromotionsCard] details load failed', loadError);
    }
  } finally {
    detailsLoading.value = false;
  }
};

onMounted(() => {
  const collapseEl = document.getElementById(collapseId.value);

  const onCollapseHidden = () => {
    mobileButtonText.value = btnText;
  };

  const onCollapseShown = () => {
    mobileButtonText.value = 'Hide Details';
  };

  if (collapseEl) {
    collapseEl.addEventListener('show.bs.collapse', loadDetails);
    collapseEl.addEventListener('hidden.bs.collapse', onCollapseHidden);
    collapseEl.addEventListener('shown.bs.collapse', onCollapseShown);
  }

  detachDetailsListeners = () => {
    if (collapseEl) {
      collapseEl.removeEventListener('show.bs.collapse', loadDetails);
      collapseEl.removeEventListener('hidden.bs.collapse', onCollapseHidden);
      collapseEl.removeEventListener('shown.bs.collapse', onCollapseShown);
    }
    detachDetailsListeners = null;
  };
});

onBeforeUnmount(() => {
  try {
    detachDetailsListeners?.();
  } catch {
    detachDetailsListeners = null;
  }
});

const copyDiscountCode = async (code, event) => {
  const copyButton = event.currentTarget; // Get the clicked button element
  if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(code);
      copyButton.textContent = 'Copied';
    } catch (error) {
      console.error('Clipboard writeText error:', error);
      copyFallback(code, copyButton);
    }
  } else {
    copyFallback(code, copyButton);
  }
};

const copyFallback = (code, copyButton) => {
  const tempInput = document.createElement('input');
  tempInput.value = code;
  document.body.appendChild(tempInput);
  tempInput.select();
  document.execCommand('copy');
  document.body.removeChild(tempInput);
  copyButton.textContent = 'Copied';
};
</script>

<template>
    <div class="card w-100 mb-sm-0 mb-2">
      <div class="row g-0">
        <div class="col-4 col-md-3 img-wrapper p-3 p-md-0">
          <NuxtImg
            :src="card?.attributes?.image?.url || 'https://via.placeholder.com/640x360'"
            :alt="promotionImageAlt"
            loading="lazy"
            decoding="async"
            class="w-100"
          />
        </div>
        <div class="col-8 col-md-9">
          <div class="card-body pt-3 pt-md-0 pe-3 pe-md-0">
            <h5 class="card-title">{{ card.attributes.title }}</h5>
            <article :class="['card-text', { 'd-md-none': card.attributes.mobileDescription }]" v-html="card.attributes.mobileDescription" v-if="card.attributes.mobileDescription"></article>
            <article :class="['card-text', { 'd-none d-md-block': card.attributes.mobileDescription }]" v-html="card.attributes.description"></article>
            <p v-if="card.attributes.promoCode" class="mt-2"><strong>CODE: {{ card.attributes.promoCode }}</strong></p>
          </div>
        </div>
      </div>
      <div class="row g-0">
        <div class="col-10 button-wrapper col-md-9 offset-md-3 offset-1 pb-3 ps-md-2 d-flex justify-content-between flex-column flex-lg-row">
          <button class="btn btn-id-primary btn-sm copy-btn me-0 mb-4 mb-lg-0" @click="copyDiscountCode(card.attributes.discountCode, $event)" v-if="card.attributes.discountCode">Copy Code</button>
          <button
            class="btn btn-outline-secondary btn-sm d-none d-sm-block"
            type="button"
            data-bs-toggle="modal"
            :data-bs-target="'#' + modalId"
            @click="loadDetails"
          >{{ card.attributes.btnText }}</button>
          <article class="mb-2 collapse" :id="collapseId">
            <div
              v-if="detailsHtml"
              v-html="detailsHtml"
            />
            <p v-else-if="detailsLoading" class="mb-0 small text-muted" aria-live="polite">
              Loading…
            </p>
            <p v-else-if="detailsFetchError" class="text-danger mb-0 small" role="alert">
              Unable to load this content. Please try again later.
            </p>
          </article>
          <button
            class="btn btn-outline-secondary ms-0 ms-sm-2 btn-sm btn-mobile d-block d-sm-none"
            type="button"
            data-bs-toggle="collapse"
            :data-bs-target="'#' + collapseId"
            aria-expanded="false"
            :aria-controls="collapseId"
          >{{ mobileButtonText }}</button>
        </div>
      </div>
    </div>
    <ClientOnly>
      <Teleport to="body">
        <UIModal :modalId="modalId" modalClass="modal-dialog-scrollable">
          <GenericContent
            v-if="detailsHtml"
            :data="{ content: detailsHtml }"
          />
          <p v-else-if="detailsLoading" class="mb-0 px-1 small text-muted" aria-live="polite">
            Loading…
          </p>
          <p v-else-if="detailsFetchError" class="text-danger mb-0 px-1 small" role="alert">
            Unable to load this content. Please try again later.
          </p>
        </UIModal>
      </Teleport>
    </ClientOnly>
</template>

<style lang="scss" scoped>
.card {
  max-width: 470px;
  border-radius: 24px;
  border: solid 1px $color-light-gray;

  

  img {
    border-radius: 12px;
    max-width: 100px;
  }

  @include media-breakpoint-up(md) {       
    padding: 30px;
  }

  .card-body {
    padding-left: 1rem;
    padding-right: 0px;
    @include media-breakpoint-down(md) {       
      padding-left: 0px;
    }
  }

  .button-wrapper button {
    font-size: 1rem;
  }
}
</style>
